import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';

export const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'tr' ? 'en' : 'tr');
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/services', label: t('nav.services') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-[#0F1A2E] shadow-lg py-2' : 'bg-[#0F1A2E]/95 py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo"
            className="flex items-center hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center gap-3">
              {/* Logo Icon */}
              <div className="text-[#C9A84C]">
                <svg width="50" height="50" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M50 5L90 25V75L50 95L10 75V25L50 5Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <path d="M50 15L80 30V70L50 85L20 70V30L50 15Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M35 45L50 35L65 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M35 55L50 45L65 55" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M35 65L50 55L65 65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              {/* Logo Text */}
              <div className="flex flex-col">
                <span className="text-[#C9A84C] font-heading text-2xl md:text-3xl font-bold tracking-wide">BEU</span>
                <span className="text-[#C9A84C]/80 text-[10px] md:text-xs tracking-widest uppercase">Finansal Danışmanlık</span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`font-body text-sm tracking-wide transition-all duration-300 hover:text-[#C9A84C] ${
                  location.pathname === link.to
                    ? 'text-[#C9A84C]'
                    : 'text-white/80'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-2 text-white/80 hover:text-[#C9A84C] transition-colors"
            >
              <Globe size={18} />
              <span className="text-sm font-medium uppercase">{i18n.language}</span>
            </button>

            {/* CTA Button - Desktop */}
            <Link
              to="/contact"
              data-testid="header-cta"
              className="hidden md:block bg-[#C9A84C] text-[#0F1A2E] px-6 py-3 font-semibold text-sm tracking-wide hover:bg-[#E5D49B] transition-all duration-300"
            >
              {t('hero.cta')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            data-testid="mobile-menu"
            className="lg:hidden bg-[#0F1A2E] border-t border-[#C9A84C]/10 mt-4 overflow-hidden"
          >
            <nav className="flex flex-col py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.to.replace('/', '') || 'home'}`}
                  className={`px-6 py-3 font-body text-base transition-all duration-300 hover:bg-[#1B2B4B]/50 hover:text-[#C9A84C] ${
                    location.pathname === link.to
                      ? 'text-[#C9A84C] bg-[#1B2B4B]/50'
                      : 'text-white/80'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mx-6 mt-4 bg-[#C9A84C] text-[#0F1A2E] px-6 py-3 font-semibold text-sm tracking-wide text-center hover:bg-[#E5D49B] transition-all duration-300"
              >
                {t('hero.cta')}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
